import type { SupabaseClient } from '@supabase/supabase-js';
import { resolveClient } from './supabase';
import { getCurrentUser } from './auth';

export interface Thread {
  id: string;
  message_id: string;
  thread_id: string;
  subject: string;
  body_html?: string;
  sent_at: string;
  from_email: string;
  to_emails: string[];
  cc_emails?: string[];
  bcc_emails?: string[];
  source: string;
  source_id: string;
}

/**
 * Get all Thread nodes for an Activity via HAS_THREAD edges
 */
export async function getThreads(activityNodeId: string, client?: SupabaseClient): Promise<Thread[]> {
  const supabase = resolveClient(client);
  const user = await getCurrentUser();

  if (!user?.tenant_id) {
    throw new Error('User not authenticated or missing tenant_id');
  }

  // Query Thread nodes linked to this Activity via HAS_THREAD edges
  const { data: edges, error: edgesError } = await supabase.from('edges').select('dst').eq('src', activityNodeId).eq('type', 'HAS_THREAD').eq('tenant_id', user.tenant_id);

  if (edgesError) {
    throw new Error(`Failed to fetch thread edges: ${edgesError.message}`);
  }

  if (!edges || edges.length === 0) {
    return [];
  }

  const threadNodeIds = edges.map((edge) => edge.dst);

  // Fetch Thread nodes
  const { data: threadNodes, error: nodesError } = await supabase
    .from('nodes')
    .select('id, props')
    .eq('tenant_id', user.tenant_id)
    .eq('type', 'Thread')
    .in('id', threadNodeIds)
    .order('props->sent_at', { ascending: true });

  if (nodesError) {
    throw new Error(`Failed to fetch thread nodes: ${nodesError.message}`);
  }

  if (!threadNodes) {
    return [];
  }

  // Map to Thread interface
  return threadNodes.map((node) => ({
    id: node.id,
    message_id: node.props.message_id,
    thread_id: node.props.thread_id,
    subject: node.props.subject,
    body_html: node.props.body_html,
    sent_at: node.props.sent_at,
    from_email: node.props.from_email,
    to_emails: node.props.to_emails,
    cc_emails: node.props.cc_emails,
    bcc_emails: node.props.bcc_emails,
    source: node.props.source,
    source_id: node.props.source_id,
  }));
}
